import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmptyState, SelectField, Stat } from "@/components/crm/parts";
import { RevenuePlanChart } from "@/components/crm/RevenuePlanChart";
import { SalesActions } from "@/components/crm/SalesActions";
import { SalesShell } from "@/components/crm/SalesShell";
import { supabase } from "@/integrations/supabase/client";
import { useSalesOwner } from "@/lib/crm/owner-filter";
import { buildRevenuePlan, monthRange, planTotals, type Grain } from "@/lib/crm/revenue-plan";
import {
  useContracts,
  useCrmInvalidate,
  useOpportunities,
  useRevenueTargets,
  useStages,
} from "@/lib/crm/useCrm";
import { useQuarterStartMonth } from "@/lib/crm/useQuarterStart";
import { usePermissions } from "@/lib/crm/usePermissions";
import { gbp, money, parseAmount } from "@/lib/crm/types";
import type { Sheet } from "@/lib/exports/file";

export const Route = createFileRoute("/_authenticated/sales/revenue-plan")({
  head: () => ({
    meta: [
      { title: "Revenue plan — MyPriorities" },
      {
        name: "description",
        content:
          "Stacked revenue by contracted, renewal and new pipeline, month by month, with Budget and Stretch Budget lines running out into the future.",
      },
      { property: "og:title", content: "Revenue plan — MyPriorities" },
      {
        property: "og:description",
        content: "Contracted, renewal and new pipeline revenue against Budget and Stretch.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: RevenuePlanPage,
});

function RevenuePlanPage() {
  return (
    <SalesShell
      title="Revenue plan"
      subtitle="Contracted, renewal and new pipeline stacked against Budget and Stretch Budget."
    >
      {({ orgId, userId }) => <RevenuePlanBoard orgId={orgId} userId={userId} />}
    </SalesShell>
  );
}

const HORIZONS = [
  { value: "12", label: "Next 12 months" },
  { value: "18", label: "Next 18 months" },
  { value: "24", label: "Next 24 months" },
  { value: "36", label: "Next 36 months" },
];

function RevenuePlanBoard({ orgId, userId }: { orgId: string; userId: string }) {
  const contracts = useContracts(orgId);
  const opportunities = useOpportunities(orgId);
  const stages = useStages(orgId);
  const targets = useRevenueTargets(orgId);
  const quarterStartMonth = useQuarterStartMonth();
  const { scope } = useSalesOwner();

  const [grain, setGrain] = useState<Grain>("month");
  const [horizon, setHorizon] = useState("12");
  const [weighted, setWeighted] = useState("weighted");

  const months = Number(horizon);
  const buckets = useMemo(
    () =>
      buildRevenuePlan({
        contracts: scope(contracts.data ?? []),
        opportunities: scope(opportunities.data ?? []),
        stages: stages.data ?? [],
        targets: (targets.data ?? []).map((t) => ({
          id: t.id,
          period_start: t.period_start,
          budget: t.budget === null ? null : money(t.budget),
          stretch: t.stretch === null ? null : money(t.stretch),
        })),
        months,
        grain,
        weighted: weighted === "weighted",
        quarterStartMonth,
      }),
    [
      contracts.data,
      opportunities.data,
      stages.data,
      targets.data,
      months,
      grain,
      weighted,
      quarterStartMonth,
      scope,
    ],
  );

  const totals = planTotals(buckets);
  const gapToBudget = totals.total - totals.budget;

  const sheets = (): Sheet[] => [
    {
      name: grain === "month" ? "By month" : "By quarter",
      rows: [
        [
          "Period",
          "Contracted",
          "Renewal",
          "New pipeline",
          "Total",
          "Budget",
          "Stretch",
          "Vs budget",
        ],
        ...buckets.map((b) => [
          b.label,
          Math.round(b.contracted),
          Math.round(b.renewal),
          Math.round(b.pipeline),
          Math.round(b.total),
          b.budget === null ? "" : Math.round(b.budget),
          b.stretch === null ? "" : Math.round(b.stretch),
          b.budget === null ? "" : Math.round(b.total - b.budget),
        ]),
      ],
      money: [1, 2, 3, 4, 5, 6, 7],
    },
  ];

  return (
    <div className="grid fill-track gap-5">
      <div className="flex min-w-0 flex-wrap items-end justify-between gap-3">
        <div className="grid min-w-0 flex-1 basis-full gap-3 sm:basis-auto sm:grid-cols-3">

          <SelectField
            label="View by"
            value={grain}
            onChange={(v) => setGrain(v as Grain)}
            options={[
              { value: "month", label: "Month" },
              { value: "quarter", label: "Quarter" },
            ]}
          />
          <SelectField label="Horizon" value={horizon} onChange={setHorizon} options={HORIZONS} />
          <SelectField
            label="Pipeline basis"
            value={weighted}
            onChange={setWeighted}
            options={[
              { value: "weighted", label: "Weighted by probability" },
              { value: "gross", label: "Gross value" },
            ]}
          />
        </div>
        <SalesActions
          exports={[{ label: "Revenue plan", baseName: "revenue-plan", sheets }]}
          visual={() => ({
            kind: "bar-stacked",
            title: "Revenue plan",
            subtitle: `Contracted, renewal and new pipeline by ${grain}, against Budget and Stretch`,
            grid: {
              headers: ["Period", "Contracted", "Renewal", "New pipeline", "Budget", "Stretch"],
              rows: buckets.map((b) => [
                b.label,
                Math.round(b.contracted),
                Math.round(b.renewal),
                Math.round(b.pipeline),
                b.budget === null ? 0 : Math.round(b.budget),
                b.stretch === null ? 0 : Math.round(b.stretch),
              ]),
            },
          })}
          sourceNote="From your Revenue plan"
        />
      </div>

      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Contracted" value={gbp(totals.contracted)} />
        <Stat label="Renewal" value={gbp(totals.renewal)} />
        <Stat label="New pipeline" value={gbp(totals.pipeline)} />
        <Stat label="Total" value={gbp(totals.total)} />
        <Stat label="Budget" value={gbp(totals.budget)} />
        <Stat
          label="Stretch"
          value={gbp(totals.stretch)}
          hint={
            totals.budget
              ? gapToBudget >= 0
                ? `${gbp(gapToBudget)} above budget`
                : `${gbp(Math.abs(gapToBudget))} short of budget`
              : "Add budget figures below"
          }
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Revenue by source, {grain === "month" ? "month by month" : "quarter by quarter"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {buckets.length ? (
            <RevenuePlanChart buckets={buckets} />
          ) : (
            <EmptyState title="Nothing to plot yet" body="Add contracts or open deals first." />
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            Contracted comes from live contracts phased by their revenue recognition settings.
            Renewal and new pipeline come from open deals, spread from their expected close date.
          </p>
        </CardContent>
      </Card>

      <TargetsEditor orgId={orgId} userId={userId} months={months} />
    </div>
  );
}

/** Monthly Budget and Stretch Budget entry, with a quick fill-forward. */
function TargetsEditor({
  orgId,
  userId,
  months,
}: {
  orgId: string;
  userId: string;
  months: number;
}) {
  const { can } = usePermissions();
  const targets = useRevenueTargets(orgId);
  const invalidate = useCrmInvalidate();
  const [fillBudget, setFillBudget] = useState("");
  const [fillStretch, setFillStretch] = useState("");
  const [busy, setBusy] = useState(false);

  const rows = monthRange(months);
  const existing = new Map(
    (targets.data ?? []).map((t) => [t.period_start.slice(0, 7), t] as const),
  );

  const upsert = async (
    periodStart: string,
    patch: { budget?: number | null; stretch?: number | null },
  ) => {
    const current = existing.get(periodStart.slice(0, 7));
    const values = {
      organisation_id: orgId,
      owner_user_id: current?.owner_user_id ?? userId,
      period_start: periodStart,
      budget: patch.budget !== undefined ? patch.budget : (current?.budget ?? null),
      stretch: patch.stretch !== undefined ? patch.stretch : (current?.stretch ?? null),
    };
    const { error } = await supabase
      .from("revenue_targets")
      .upsert(values, { onConflict: "organisation_id,period_start" });
    if (error) toast.error(error.message);
    else {
      invalidate();
      void targets.refetch();
    }
  };

  const fillForward = async () => {
    const budget = parseAmount(fillBudget);
    const stretch = parseAmount(fillStretch);
    if (budget === null && stretch === null) {
      toast.error("Enter a budget or stretch figure to apply");
      return;
    }
    setBusy(true);
    const payload = rows.map((period_start) => {
      const current = existing.get(period_start.slice(0, 7));
      return {
        organisation_id: orgId,
        owner_user_id: current?.owner_user_id ?? userId,
        period_start,
        budget: budget ?? current?.budget ?? null,
        stretch: stretch ?? current?.stretch ?? null,
      };
    });
    const { error } = await supabase
      .from("revenue_targets")
      .upsert(payload, { onConflict: "organisation_id,period_start" });
    setBusy(false);
    if (error) toast.error(error.message);
    else {
      invalidate();
      void targets.refetch();
      toast.success(`Applied to ${rows.length} months`);
    }
  };

  if (!can("manage_revenue_plan"))
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Budget and stretch</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Your access level can view the plan but not set the targets. An admin can change that in
            the Team hub under Permissions.
          </p>
        </CardContent>
      </Card>
    );

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Budget and Stretch Budget</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border p-3">
          <div className="grid gap-1">
            <span className="text-xs text-muted-foreground">Budget per month</span>
            <Input
              value={fillBudget}
              onChange={(e) => setFillBudget(e.target.value)}
              placeholder="e.g. 250000"
              className="h-9 w-36"
            />
          </div>
          <div className="grid gap-1">
            <span className="text-xs text-muted-foreground">Stretch per month</span>
            <Input
              value={fillStretch}
              onChange={(e) => setFillStretch(e.target.value)}
              placeholder="e.g. 300000"
              className="h-9 w-36"
            />
          </div>
          <Button size="sm" onClick={() => void fillForward()} disabled={busy}>
            {busy ? "Applying…" : `Apply to next ${rows.length} months`}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Set a baseline, then tweak individual months below.
          </p>
        </div>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((period) => {
            const current = existing.get(period.slice(0, 7));
            const label = new Date(`${period}T00:00:00Z`).toLocaleDateString("en-GB", {
              month: "short",
              year: "numeric",
              timeZone: "UTC",
            });
            return (
              <div key={period} className="rounded-lg border border-border p-2">
                <p className="text-xs font-medium">{label}</p>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <MoneyInput
                    placeholder="Budget"
                    value={current?.budget ?? null}
                    onCommit={(budget) => void upsert(period, { budget })}
                  />
                  <MoneyInput
                    placeholder="Stretch"
                    value={current?.stretch ?? null}
                    onCommit={(stretch) => void upsert(period, { stretch })}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function MoneyInput({
  value,
  placeholder,
  onCommit,
}: {
  value: number | null;
  placeholder: string;
  onCommit: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const [focused, setFocused] = useState(false);
  const shown = focused ? draft : value === null ? "" : String(value);

  return (
    <Input
      value={shown}
      placeholder={placeholder}
      className="h-8"
      onFocus={() => {
        setDraft(value === null ? "" : String(value));
        setFocused(true);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setFocused(false);
        const next = parseAmount(draft);
        if (next !== value) onCommit(next);
      }}
    />
  );
}
